import { FC, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Button } from '@kizen/kds/Button';
import { Icon } from '@kizen/kds/Icon';
import ButtonWithDropdown from '__components/Button/WithDropdown';
import {
  MONO_FONT,
  StatusIcon,
  ToolDetail,
  ToolName,
  ToolRowStatic,
  ToolTitle,
} from 'ts-components/PiChat/styles';
import { Chevron } from 'ts-components/PiChat/fileTree/styles';
import { COMPOSER_INPUT_ID } from 'ts-components/PiChat/PiChatComposer';
import {
  approvalView,
  type ApprovalCardRecord,
  type ApprovalView,
} from './approvalController';
import { approvalCopy } from './approvalCopy';
import {
  approvalFooter,
  approvalHeading,
  planDisplay,
  resolvedLine,
  segmentsText,
  shouldTakeFocus,
  type ApprovalButtonId,
  type OperationRowDisplay,
  type PlanDisplay,
  type ResolvedLine,
  type SummarySegment,
} from './approvalPresentation';

const QA_PREFIX = 'kicode-approval';

/*
 * Every text element here sets its size in px: the app's root font size is
 * 10px, and an element without its own size computed to it.
 */

/** Its own card under the waiting tool row: the chat's tool-card frame. */
const Card = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  border: 1px solid var(--color-border-medium-light);
  border-radius: 4px;
  background: var(--color-background-white);
  font-size: 13px;
  line-height: 18px;
  color: var(--color-font-primary);

  /* Focus moved here programmatically shows a ring only for keyboard users. */
  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid var(--color-button-secondary-default);
    outline-offset: 2px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 18px;
`;

const Heading = styled.div`
  padding-bottom: 8px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: var(--color-font-primary);
`;

/** Long requests scroll inside the card; the chat keeps following the bottom. */
const ListScroll = styled.div`
  max-height: 320px;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
`;

/** One line per change, hairlines between. */
const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 13px;
  line-height: 18px;

  & > li {
    font: inherit;
    border-top: 1px solid var(--color-border-medium-light);
  }
`;

const Toggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  min-height: 32px;
  margin: 0;
  padding: 7px 2px;
  border: none;
  background: none;
  color: var(--color-font-primary);
  font: inherit;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--color-background-hover);
  }
`;

/** A line that isn't a toggle: a multi-change plan's summary, or a command. */
const StaticLine = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 32px;
  padding: 7px 2px;
  font-size: 13px;
  line-height: 18px;
  overflow-wrap: anywhere;
`;

const CommandText = styled.span`
  font-family: ${MONO_FONT};
  font-size: 12px;
  line-height: 18px;
  white-space: pre-wrap;
`;

/** The name, then (for a sentence) any wrapped text; never truncated. */
const Text = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  font-size: 13px;
  line-height: 18px;
  overflow-wrap: anywhere;
`;

const SegmentText = styled.span`
  font: inherit;
`;

/** The name or sentence line inside `Text`. */
const Line = styled.span`
  font: inherit;
`;

/** An api name: the identifier styling shared by the summary and the expanded detail. */
const ApiName = styled.code`
  font-family: ${MONO_FONT};
  font-size: 12px;
  font-weight: 400;
  line-height: inherit;
  color: var(--color-font-secondary);
  background: none;
  padding: 0;
`;

const Verb = styled.span`
  font: inherit;
  color: var(--color-font-secondary);
`;

/** Quiet right-aligned detail, such as a field type. */
const Meta = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-font-placeholder);
`;

/**
 * A destructive change's verb, and its inline note: the line's own size and
 * weight in the error colour; no icon, tint or second line.
 */
const Danger = styled.span`
  font: inherit;
  color: var(--color-feedback-error-foreground);
`;

/** " — replaces options": after the name, when the red verb alone doesn't say it. */
const Note: FC<{ note: string | null }> = ({ note }) =>
  note ? <Danger> — {note}</Danger> : null;

const Trailing = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
`;

const Detail = styled(ToolDetail)`
  margin: 0 0 10px;
  border: 1px solid var(--color-border-medium-light);
  border-radius: 4px;

  /* The api name keeps the payload's size here. */
  & code {
    font-size: inherit;
  }
`;

const DetailKeyLabel = styled.span`
  font: inherit;
  color: var(--color-font-placeholder);
`;

/** Under a truncated plan: the meta style on its own line. */
const HiddenNote = styled.div`
  padding: 8px 2px;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-font-placeholder);
`;

/** Footer: quiet surface, Decline then the Approve split button, right-aligned. */
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  padding: 10px 12px;
  border-top: 1px solid var(--color-border-medium-light);
  border-radius: 0 0 4px 4px;
  background: var(--color-background-lightest);
`;

/**
 * The split button's own styles size its label in em for the app's 10px
 * root (1.2em → 12px), so its slot sets that base explicitly.
 */
const SplitSlot = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  font-size: 10px;
  line-height: normal;

  /*
   * Open the menu upward: below, the chat's scroll area clips it. The menu's
   * container sits on the button's top edge (bottomOffset), and the menu
   * grows up from there instead of down.
   */
  & .ButtonMenu__menu.ButtonMenu__menu {
    top: auto;
    bottom: 100%;
  }
`;

/** Puts the split button's menu container on the button's top edge (its 36px height). */
const MENU_ABOVE_OFFSET = 36;

const Segments: FC<{ segments: readonly SummarySegment[] }> = ({
  segments,
}) => (
  <>
    {segments.map((segment, index) =>
      // Segments are a fixed sequence for one summary; the index is stable.
      segment.code ? (
        <ApiName key={index}>{segment.text}</ApiName>
      ) : segment.danger ? (
        <Danger key={index}>{segment.text}</Danger>
      ) : (
        <SegmentText key={index}>{segment.text}</SegmentText>
      )
    )}
  </>
);

const DetailBody: FC<{ apiKey?: string; detail?: string }> = ({
  apiKey,
  detail,
}) => {
  const { t } = useTranslation();
  return (
    <>
      {apiKey !== undefined ? (
        <>
          <DetailKeyLabel>{approvalCopy(t).apiName} </DetailKeyLabel>
          <ApiName>{apiKey}</ApiName>
        </>
      ) : null}
      {apiKey !== undefined && detail !== undefined ? '\n\n' : null}
      {detail}
    </>
  );
};

/** One change in a multi-change plan: the whole line is the toggle. */
const OperationRow: FC<{ row: OperationRowDisplay }> = ({ row }) => {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <Toggle
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        data-qa-id={`${QA_PREFIX}-operation-toggle`}
      >
        <Text>
          <Line>
            {row.verbSegments ? (
              <Verb>
                <Segments segments={row.verbSegments} />{' '}
              </Verb>
            ) : null}
            {row.nameIsApiName ? (
              <ApiName>{row.name}</ApiName>
            ) : (
              <SegmentText>{row.name}</SegmentText>
            )}
            <Note note={row.note} />
          </Line>
        </Text>
        <Trailing>
          {row.typeLabel ? <Meta>{row.typeLabel}</Meta> : null}
          <Chevron $expanded={open} aria-hidden />
        </Trailing>
      </Toggle>
      {open ? (
        <Detail>
          <DetailBody apiKey={row.key} detail={row.detail} />
        </Detail>
      ) : null}
    </li>
  );
};

/** A one-change plan: its sentence is the line and the toggle. */
const SingleChange: FC<{ plan: PlanDisplay }> = ({ plan }) => {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <Toggle
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        data-qa-id={`${QA_PREFIX}-plan-toggle`}
      >
        <Text>
          <Line>
            <Segments segments={plan.summarySegments} />
            <Note note={plan.summaryNote} />
          </Line>
        </Text>
        <Trailing>
          <Chevron $expanded={open} aria-hidden />
        </Trailing>
      </Toggle>
      {open ? (
        <Detail>
          <DetailBody apiKey={plan.summaryKey} detail={plan.summaryDetail} />
        </Detail>
      ) : null}
    </li>
  );
};

/** A plan's lines: one for a single change, else its summary then a line per change. */
const PlanLines: FC<{ plan: PlanDisplay }> = ({ plan }) => (
  <>
    {plan.rows.length ? (
      <>
        <li>
          <StaticLine>
            <Text>
              <Line>
                <Segments segments={plan.summarySegments} />
              </Line>
            </Text>
          </StaticLine>
        </li>
        {plan.rows.map((row) => (
          <OperationRow key={row.id} row={row} />
        ))}
      </>
    ) : (
      <SingleChange plan={plan} />
    )}
    {plan.hiddenNote ? (
      <li>
        <HiddenNote data-qa-id={`${QA_PREFIX}-hidden-operations`}>
          {plan.hiddenNote}
        </HiddenNote>
      </li>
    ) : null}
  </>
);

const PendingCard: FC<{
  view: ApprovalView;
  onPress: (id: ApprovalButtonId) => void;
}> = ({ view, onPress }) => {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const plans =
    view.kind === 'plans' ? view.plans.map((plan) => planDisplay(t, plan)) : [];
  const label = approvalCopy(t).cardLabel(
    view.kind === 'command'
      ? view.commandLine
      : plans.map((plan) => segmentsText(plan.summarySegments)).join(' · ')
  );
  const footer = approvalFooter(t, view);
  const windowAction = footer.approveWindow;

  // Read during the first render, before the commit that swaps the locked
  // composer's textarea out: by layout time focus has already fallen to <body>.
  const [composerHadFocus] = useState(
    () => document.activeElement?.id === COMPOSER_INPUT_ID
  );
  // Focus lands on the card itself, not Approve: a user mid-sentence would
  // otherwise approve with their next space or Enter.
  useLayoutEffect(() => {
    const active = document.activeElement;
    if (
      shouldTakeFocus({
        composerHadFocus,
        focusNow:
          !active || active === document.body
            ? 'body'
            : active.id === COMPOSER_INPUT_ID
              ? 'composer'
              : 'elsewhere',
      })
    ) {
      cardRef.current?.focus({ preventScroll: true });
    }
  }, [composerHadFocus]);

  return (
    <Card
      ref={cardRef}
      role="group"
      aria-label={label}
      tabIndex={-1}
      data-qa-id={QA_PREFIX}
    >
      <Body>
        <Heading>{approvalHeading(t, view)}</Heading>
        <ListScroll
          role="region"
          aria-label={approvalCopy(t).changes}
          // Focusable so a long list can be scrolled from the keyboard.
          tabIndex={0}
          data-qa-id={`${QA_PREFIX}-rows`}
        >
          <List>
            {view.kind === 'command' ? (
              <li>
                <StaticLine>
                  <CommandText>{view.commandLine}</CommandText>
                </StaticLine>
              </li>
            ) : (
              plans.map((plan, index) => (
                // Plans never reorder within a request; the index keeps duplicate planIds distinct.
                <PlanLines key={`${index}:${plan.planId}`} plan={plan} />
              ))
            )}
          </List>
        </ListScroll>
      </Body>
      <Footer>
        <Button
          variant="text"
          color="warning"
          size="small"
          onClick={() => onPress(footer.decline.id)}
          qa={{ id: `${QA_PREFIX}-${footer.decline.id}` }}
        >
          {footer.decline.label}
        </Button>
        {windowAction ? (
          <SplitSlot>
            <ButtonWithDropdown
              noSpace
              color="green"
              onClick={() => onPress(footer.approve.id)}
              onChange={() => onPress(windowAction.id)}
              options={[
                {
                  // The option renders with data-qa={value}.
                  value: `${QA_PREFIX}-${windowAction.id}`,
                  label: windowAction.label,
                },
              ]}
              restrictWidth={false}
              bottomOffset={MENU_ABOVE_OFFSET}
              data-qa-id={`${QA_PREFIX}-${footer.approve.id}`}
            >
              {footer.approve.label}
            </ButtonWithDropdown>
          </SplitSlot>
        ) : (
          <Button
            color="primary"
            onClick={() => onPress(footer.approve.id)}
            qa={{ id: `${QA_PREFIX}-${footer.approve.id}` }}
          >
            {footer.approve.label}
          </Button>
        )}
      </Footer>
    </Card>
  );
};

/** The tool row's own status icons: done, running, failed, and closed for the rest. */
const RESOLVED_ICON = {
  approved: { icon: 'custom-circle-check', color: 'feedback-success' },
  sending: { icon: 'action-loading', color: 'font-placeholder' },
  failed: { icon: 'message-warning-triangle', color: 'feedback-error' },
  other: { icon: 'action-close', color: 'font-placeholder' },
} as const satisfies Record<
  ResolvedLine['tone'],
  { icon: string; color: string }
>;

export interface ApprovalCardProps {
  card: ApprovalCardRecord;
  onPress: (id: ApprovalButtonId) => void;
}

/** A pending request's card, or the one line it collapses to once settled. */
export const ApprovalCard: FC<ApprovalCardProps> = ({ card, onPress }) => {
  const { t } = useTranslation();
  if (card.outcome === 'pending') {
    return <PendingCard view={approvalView(card)} onPress={onPress} />;
  }
  const line = resolvedLine(t, { ...card, outcome: card.outcome });
  return (
    // The chat's own tool row, so it matches the rows around it: the outcome
    // where the tool name goes, the summary where a tool's display title goes.
    <ToolRowStatic data-qa-id={`${QA_PREFIX}-${card.outcome}`}>
      <StatusIcon aria-hidden>
        <Icon {...RESOLVED_ICON[line.tone]} size="sm" />
      </StatusIcon>
      <ToolName>{line.outcome}</ToolName>
      <ToolTitle title={line.summary}>{line.summary}</ToolTitle>
    </ToolRowStatic>
  );
};
